import { PC_13663, RFI, SD_LANGUAGE } from "./data";
import { C } from "./theme";
import { Callout, Card, Eyebrow, Section, SubSection } from "./ui";

// ===================================================================
// 15. Recommendations for the competitive solicitation
// ===================================================================

type Req = { req: string; why: string };

const TECHNICAL: Req[] = [
  {
    req: "The system must combine multiple recordings, including officer narration, into one report",
    why: "Body-worn camera, in-car video, recorded interviews, and officer dictation are different evidence, and a report built from only one of them is incomplete. Our platform will work from interaction audio alone, and we support that, but we strongly recommend pairing it with first-person narration because footage cannot capture what the officer knew, perceived, or decided. Ask every vendor directly: when a narrative is drafted from body-worn footage alone, how does your product handle the elements the recording never captured?",
  },
  {
    req: "Speaker separation, with officer-controlled speaker naming",
    why: "Statements attributed to the wrong person in a report are a discovery problem. The officer must be able to correct labels.",
  },
  {
    req: "The transcript must be readable, editable, and the narrative regenerable from it",
    why: "If the officer cannot see and fix what the system heard, they cannot certify the report that came from it.",
  },
  {
    req: "Retention of the first draft and the source recordings",
    why: `Required by ${PC_13663.codeSection}(b)(1) and (c). Ask each vendor to state where the first draft is stored and how the department obtains it.`,
  },
  {
    req: "Automatic AI-use disclosure that an officer cannot silently omit",
    why: `${PC_13663.codeSection}(a). A disclosure that depends on the officer remembering to add it will be missing from some reports.`,
  },
  {
    req: "Multi-factor authentication and single sign-on",
    why: "Baseline CJIS expectation and a prerequisite for any system touching criminal justice information.",
  },
  {
    req: "Deployment in a government-controlled cloud environment",
    why: "Specify the environment rather than accepting a general assurance of security.",
  },
  {
    req: "Disclosure of every foundation AI model and subprocessor used",
    why: `Without it the City cannot verify the ${PC_13663.codeSection}(d)(1) restrictions actually flow down.`,
  },
  {
    req: "Export in open formats, and no coupling to a single records system",
    why: "The department should be able to change its RMS without changing its report-writing platform, and vice versa.",
  },
  {
    req: `Multilingual capability sized to San Diego, not to a demo`,
    why: `${SD_LANGUAGE.nonEnglishAtHomePct}% of San Diego residents speak a language other than English at home (U.S. Census Bureau, American Community Survey 2024 1-year estimates, City of San Diego, table S1601, population 5 years and over, ±${SD_LANGUAGE.marginOfErrorPct} pp). Require a specific list of supported languages, not the word "multilingual."`,
  },
];

const BUSINESS: Req[] = [
  {
    req: "Flat per-officer pricing, with no metering of audio, reports, or transcription",
    why: "Metered pricing charges the department for the behavior it is trying to encourage, and makes bids impossible to compare without a usage volume nobody has yet.",
  },
  {
    req: "A measured pilot as part of evaluation",
    why: "Adoption is the variable that determines results. A scripted demonstration measures the vendor's preparation, not the product.",
  },
  {
    req: "Full data export on termination, in usable formats",
    why: "Including source recordings, transcripts, retained drafts, and audit records.",
  },
  {
    req: "Transition assistance and source-code escrow",
    why: "Reasonable protection when a core workflow depends on a vendor.",
  },
];

const LEGAL: Req[] = [
  {
    req: "CJIS security addendum executed by the vendor and every subprocessor",
    why: "Not just the prime contractor.",
  },
  {
    req: `Express ${PC_13663.codeSection}(d) flow-down`,
    why: "The statutory limit on selling, sharing, or otherwise using agency data must bind every party in the chain, in writing.",
  },
  {
    req: "An affirmative warranty that agency data is not used to train any model",
    why: "State it as a warranty with a remedy, not as a policy statement on a website that can change.",
  },
  {
    req: "Agency ownership of all data and all outputs",
    why: "Including reports, transcripts, and drafts. Ask every vendor how long they retain agency data after termination, and require the answer in writing. Ownership means little if a copy persists on the vendor's infrastructure indefinitely once the contract ends.",
  },
  {
    req: "Discovery and subpoena support obligations, with response timelines",
    why: "The department will need retained drafts and audit records produced on a court's schedule, not the vendor's.",
  },
  {
    req: "Availability of a declaration or testimony on how a report was produced",
    why: "In the case that a court asks, the answer should not depend on the vendor's goodwill that week.",
  },
  {
    req: "Breach notification terms and IP indemnification",
    why: "Standard, but worth specifying rather than negotiating later.",
  },
];

/**
 * RFI § II.B.4 names four categories: technical, business, legal, and revenue.
 * Revenue is the one most respondents will fold into pricing; keeping it
 * separate is both more responsive and a place to constrain data monetization.
 */
const REVENUE: Req[] = [
  {
    req: "Vendor revenue must derive from the license, not from the data",
    why: "No monetization of agency content in any form, including aggregated or de-identified derivatives, and no revenue-sharing arrangements involving agency data with any third party.",
  },
  {
    req: "Statutorily required functionality cannot be a paid add-on",
    why: `AI-use disclosure, first-draft retention, and audit trail exist because ${PC_13663.codeSection} requires them. A vendor that prices them separately is charging the department to obey the law.`,
  },
  {
    req: "No revenue from metered usage",
    why: "Usage-based revenue and thorough documentation pull in opposite directions. The vendor earns more precisely when the officer captures more, which is the behavior the department wants and should not be taxing.",
  },
  {
    req: "Renewal escalation disclosed and capped at signature",
    why: "A low first-year price with an uncapped renewal is a deferred cost, not a saving.",
  },
  {
    req: "Professional services and integration work priced at the outset",
    why: "So that a connector the department needs does not become a change order later.",
  },
];

const VENDOR_QUESTIONS = [
  "Does your product require the officer to provide their own account of the incident, or can it generate a complete narrative from body-worn camera footage alone?",
  "Do you, or any subprocessor you use, train models on agency data? Will you warrant that in the contract?",
  "In what physical environment does AI processing occur, and under what contract?",
  "Do you retain the first AI-generated draft? Where is it stored, how long, and how does the department obtain a copy?",
  "Can the officer read the transcript, correct it, and regenerate the report from the correction?",
  "Is the AI-use disclosure applied automatically, or does it depend on the officer adding it?",
  "Is transcription metered? Does the department pay more when officers capture more audio?",
  "Can you produce a complete audit trail for one specific report, on subpoena, within a court's timeline?",
  "Exactly which languages do you support for transcription? Provide the list.",
  "What happens to our data, and our retained drafts, if we terminate?",
];

export function RecommendationsSection() {
  return (
    <Section
      id="rfp"
      number="15"
      title="What we recommend the City require"
      subtitle={
        <>
          The RFI asks respondents to describe the technical, business, and legal
          specifications the City should consider when structuring a competitive
          solicitation. This is our answer. We have tried to write requirements that are
          right for the department rather than requirements that are convenient for us.
          The City can check that by asking every respondent to answer the questions at
          the end.
        </>
      }
    >
      <ReqGroup title="Technical" items={TECHNICAL} color={C.primary} />
      <ReqGroup title="Business and commercial" items={BUSINESS} color={C.accent} />
      <ReqGroup title="Legal and contractual" items={LEGAL} color={C.interaction} />
      <ReqGroup title="Revenue model" items={REVENUE} color={C.good} />

      <SubSection
        title="How to evaluate, not just what to require"
        subtitle="The evaluation method matters as much as the requirement list."
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Card title="Pilot with real officers in the field">
            Not a vendor-prepared sample and not a stack of archived recordings. Put the
            product in the hands of at least ten officers working their normal assignments
            for at least thirty days. The difference between products shows up on messy
            audio, on a bad night, at the end of a shift.
          </Card>
          <Card title="Measure four things">
            Time to complete a report, report quality, supervisor kick-back rate, and
            officer adoption at sixty days.
          </Card>
          <Card title="Baseline first">
            Measure the current state before go-live. Without a baseline, no vendor's claim
            can be tested.
          </Card>
          <Card title="Ask officers">
            Adoption is voluntary in practice regardless of policy. Survey the pilot
            division.
          </Card>
        </div>
      </SubSection>

      <SubSection
        title="Ten questions to ask every respondent"
        subtitle="Including us. If a vendor cannot answer these plainly and in writing, that is the finding."
      >
        <div className="rounded-lg border-2 border-slate-900 p-5 md:p-6 bg-slate-50">
          <ol className="space-y-3">
            {VENDOR_QUESTIONS.map((q, i) => (
              <li key={q} className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-[11px] font-bold tabular-nums">
                  {i + 1}
                </span>
                <span className="text-sm text-slate-800 leading-relaxed pt-0.5">{q}</span>
              </li>
            ))}
          </ol>
        </div>
        <p className="text-xs text-slate-500 mt-3 leading-relaxed max-w-3xl">
          We will answer all ten in writing, on the record, as part of any response to a
          competitive solicitation, and we are happy for the City to hold us to those
          answers contractually.
        </p>
      </SubSection>
    </Section>
  );
}

function ReqGroup({ title, items, color }: { title: string; items: Req[]; color: string }) {
  return (
    <div className="mt-8">
      <div className="flex items-center gap-2.5 mb-3">
        <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: color }} />
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        <span className="text-xs text-slate-400 font-mono tabular-nums">
          {items.length} recommendations
        </span>
      </div>
      <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
        {items.map((r) => (
          <div key={r.req} className="p-4 break-inside-avoid">
            <div className="flex gap-3">
              <span
                className="shrink-0 w-1.5 h-1.5 rounded-full mt-2"
                style={{ backgroundColor: color }}
              />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 leading-snug">
                  {r.req}
                </div>
                <div className="text-xs text-slate-600 leading-relaxed mt-1">{r.why}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===================================================================
// 16. Next steps
// ===================================================================

export function ContactSection() {
  return (
    <Section
      id="contact"
      title="Next steps"
      subtitle={
        <>
          This RFI does not award anything, and we have written this briefing on that
          assumption, as an argument about what the department should require rather than a
          sales document. Three things we would welcome.
        </>
      }
    >
      <div className="grid md:grid-cols-3 gap-4">
        <Card title="An oral presentation" tag="§ II.B.5">
          The RFI reserves the right to request one. We would use it to walk command staff
          through a real report end to end: recording, transcript, first draft, critique,
          attested export.
        </Card>
        <Card title="A pilot with real officers in the field">
          Give us a division, at least ten officers on their normal assignments, and at
          least thirty days. Measure the baseline first. We would rather be evaluated on
          that than on this document.
        </Card>
        <Card title="A conversation about the RFP">
          The most useful thing we can offer at this stage is help writing a solicitation
          that produces comparable bids, including from our competitors.
        </Card>
      </div>

      <div className="mt-8">
        <Callout tone="legal" title="The statute is already running">
          <p>
            {PC_13663.codeSection} has been in effect since {PC_13663.effectiveDate}. Its
            obligations attach to the department whether or not a sanctioned tool exists,
            and they attach to reports being written right now.
          </p>
          <p>
            Whatever the City ultimately procures, and from whomever, we would encourage
            SDPD to treat the disclosure, first-draft retention, and audit-trail
            requirements as a near-term operational question rather than something that
            begins when a contract is signed.
          </p>
        </Callout>
      </div>

      <div className="mt-8 rounded-lg border border-slate-200 p-6">
        <Eyebrow>Contact</Eyebrow>
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <div className="text-sm font-semibold text-slate-900">Police Narratives AI</div>
            <div className="text-sm text-slate-600 mt-1">
              <a
                href="mailto:michael@policenarratives.ai"
                className="text-blue-700 hover:text-blue-900 underline underline-offset-2"
              >
                michael@policenarratives.ai
              </a>
            </div>
            <p className="text-xs text-slate-500 mt-3 leading-relaxed">
              For {PC_13663.codeSection} draft production requests, agencies already using
              the platform write to{" "}
              <a
                href="mailto:audits@policenarratives.ai"
                className="text-blue-700 hover:text-blue-900 underline underline-offset-2"
              >
                audits@policenarratives.ai
              </a>
              .
            </p>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">
              Submitted in response to
            </div>
            <div className="text-sm text-slate-700 leading-relaxed">
              RFI {RFI.number}: {RFI.title}
              <br />
              {RFI.agency}
              <br />
              <span className="text-slate-500">Closing {RFI.closingDate}</span>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}
